import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/OurStory.css';

import QuoteModal from './QuoteModal';

import storyImage1 from '../assets/pr3.png';
import storyImage2 from '../assets/pr4.png';
import quoteIcon from '../assets/iso.png';

/* Real Indian flag - avoids Windows showing "IN" */
import indiaFlag from '../assets/india.svg';

const OurStory = () => {
  const navigate = useNavigate();

  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const openQuoteModal = () => {
    setIsQuoteModalOpen(true);
  };

  const closeQuoteModal = () => {
    setIsQuoteModalOpen(false);
  };

  const handleContactSales = () => {
    navigate('/contact');
  };

  /* ============================================
     MILESTONES
  ============================================ */

  const milestones = [
    {
      year: '2026',
      title: 'Company Founded',
      description:
        'Preferred Audiology Pvt. Ltd. was established with a vision to transform the hearing aid industry in India.',
      icon: '🏭',
      iconType: 'emoji'
    },

    {
      year: '2026',
      title: 'First Product Launch',
      description:
        "Launched QuickFit series - India's most affordable rechargeable hearing aids.",
      icon: '🦻',
      iconType: 'emoji'
    },

    {
      year: '2026',
      title: 'BYOB Program Launch',
      description:
        'Introduced revolutionary "Build Your Own Brand" concept for distributors and clinics.',
      icon: '🎨',
      iconType: 'emoji'
    },

    {
      year: '2026',
      title: 'Pan-India Expansion',
      description:
        'Expanded service network to 25+ cities across India.',
      icon: indiaFlag,
      iconType: 'image'
    }
  ];

  /* ============================================
     VALUES
  ============================================ */

  const values = [
    {
      title: 'Innovation First',
      description:
        'We constantly push boundaries to bring new concepts like Instant Service & Replacement to the market.',
      icon: '💡',
      iconType: 'emoji'
    },

    {
      title: 'Quality Commitment',
      description:
        'Every product undergoes rigorous testing to ensure international quality standards.',
      icon: '✅',
      iconType: 'emoji'
    },

    {
      title: 'Partner Success',
      description:
        'Your growth is our success. We provide complete support to help your business thrive.',
      icon: '🤝',
      iconType: 'emoji'
    },

    {
      title: 'Made in India',
      description:
        'Proudly manufacturing in India to serve Indian ears with world-class technology.',
      icon: indiaFlag,
      iconType: 'image'
    }
  ];

  /* ============================================
     ICON RENDERER
  ============================================ */

  const renderIcon = (item, className = '') => {
    if (item.iconType === 'image') {
      return (
        <img
          src={item.icon}
          alt="Indian National Flag"
          className={`our-story-india-flag ${className}`}
          loading="lazy"
        />
      );
    }

    return (
      <span className={className} aria-hidden="true">
        {item.icon}
      </span>
    );
  };

  return (
    <>
      <section
        id="our-story"
        className="our-story-section"
      >
        <div className="our-story-container">

          {/* =====================================
              SECTION HEADER
          ====================================== */}

          <div className="our-story-header">

            <span className="our-story-tagline">
              Our Journey
            </span>

            <h2 className="our-story-title">
              The{' '}
              <span className="our-story-highlight">
                Story
              </span>{' '}
              Behind Preferred Audiology
            </h2>

            <div className="our-story-underline"></div>

            <p className="our-story-subtitle">
              From a vision to India's most innovative hearing aid
              manufacturer — our journey of transforming hearing
              healthcare.
            </p>

          </div>


          {/* =====================================
              STORY CONTENT
          ====================================== */}

          <div className="our-story-content">

            {/* LEFT SIDE */}

            <div className="our-story-text">

              <div className="our-story-quote">

                <img
                  src={quoteIcon}
                  alt="ISO Certification"
                  className="our-story-quote-icon"
                />

                <p>
                  We saw a problem. Patients waited weeks for hearing
                  aid repairs. Clinics struggled with high costs.
                  Distributors had no brand identity. So we decided
                  to fix it.
                </p>

              </div>


              <p className="our-story-paragraph">
                Preferred Audiology was born with a single mission —
                to make quality hearing aids affordable and accessible
                to every Indian while empowering businesses to grow.
              </p>


              <p className="our-story-paragraph">
                We introduced two revolutionary concepts that changed
                the industry forever —
                <strong> Instant Service & Replacement</strong> at
                clinics (never done before in India) and the{' '}
                <strong>BYOB (Build Your Own Brand)</strong> program
                for distributors and clinics.
              </p>


              {/* INSTANT SERVICE */}

              <div className="our-story-highlight-box">

                <span
                  className="our-story-highlight-icon"
                  aria-hidden="true"
                >
                  ⚡
                </span>

                <div>

                  <h4>
                    The "Instant Service" Revolution
                  </h4>

                  <p>
                    No more sending products back to us. Your clinic
                    becomes the service center. Customers get instant
                    replacement. You earn their trust forever.
                  </p>

                </div>

              </div>


              {/* BYOB */}

              <div className="our-story-highlight-box">

                <span
                  className="our-story-highlight-icon"
                  aria-hidden="true"
                >
                  🎨
                </span>

                <div>

                  <h4>
                    The BYOB Movement
                  </h4>

                  <p>
                    Your brand. Your identity. Your success. We help
                    clinics and distributors create their own hearing
                    aid brand with custom packaging and logo.
                  </p>

                </div>

              </div>

            </div>


            {/* =====================================
                RIGHT SIDE IMAGES
            ====================================== */}

            <div className="our-story-image-wrapper">

              <div className="our-story-main-image">

                <img
                  src={storyImage1}
                  alt="Preferred Audiology Journey"
                  loading="lazy"
                />

              </div>


              <div className="our-story-small-image">

                <img
                  src={storyImage2}
                  alt="Preferred Audiology Manufacturing Facility"
                  loading="lazy"
                />

              </div>

            </div>

          </div>


          {/* =====================================
              MILESTONES
          ====================================== */}

          <div className="our-story-milestones">

            <h3 className="our-story-milestones-title">
              Our Journey So Far
            </h3>


            <div className="our-story-timeline">

              {milestones.map((milestone, index) => (

                <div
                  key={`${milestone.title}-${index}`}
                  className="our-story-timeline-item"
                >

                  <div className="our-story-timeline-icon">
                    {renderIcon(milestone)}
                  </div>


                  <div className="our-story-timeline-content">

                    <span className="our-story-timeline-year">
                      {milestone.year}
                    </span>

                    <h4>
                      {milestone.title}
                    </h4>

                    <p>
                      {milestone.description}
                    </p>

                  </div>


                  {index < milestones.length - 1 && (
                    <div className="our-story-timeline-line"></div>
                  )}

                </div>

              ))}

            </div>

          </div>


          {/* =====================================
              OUR VALUES
          ====================================== */}

          <div className="our-story-values">

            <h3 className="our-story-values-title">
              What Drives Us
            </h3>


            <div className="our-story-values-grid">

              {values.map((value, index) => (

                <div
                  key={`${value.title}-${index}`}
                  className="our-story-value-card"
                >

                  <div className="our-story-value-icon">
                    {renderIcon(value)}
                  </div>

                  <h4>
                    {value.title}
                  </h4>

                  <p>
                    {value.description}
                  </p>

                </div>

              ))}

            </div>

          </div>


          {/* =====================================
              VISION & MISSION
          ====================================== */}

          <div className="our-story-vision-mission">

            {/* VISION */}

            <div className="our-story-vision">

              <div
                className="our-story-vision-icon"
                aria-hidden="true"
              >
                👁️
              </div>

              <h3>
                Our Vision
              </h3>

              <p>
                To be India's most trusted hearing aid partner,
                empowering 1 million Indians with better hearing
                by 2030.
              </p>

            </div>


            {/* MISSION */}

            <div className="our-story-mission">

              <div
                className="our-story-mission-icon"
                aria-hidden="true"
              >
                🎯
              </div>

              <h3>
                Our Mission
              </h3>

              <p>
                To make quality hearing aids affordable, accessible,
                and profitable for partners through innovation
                and support.
              </p>

            </div>

          </div>


          {/* =====================================
              CTA
          ====================================== */}

          <div className="our-story-cta">

            <div className="our-story-cta-content">

              <h3>
                Be Part of Our Growth Story
              </h3>

              <p>
                Join our successful partners across India
              </p>


              <div className="our-story-cta-buttons">

                <button
                  type="button"
                  onClick={openQuoteModal}
                  className="our-story-cta-primary"
                >
                  Become a Partner

                  <svg
                    className="our-story-cta-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 12H19M19 12L13 6M19 12L13 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>

                </button>


                <button
                  type="button"
                  onClick={handleContactSales}
                  className="our-story-cta-secondary"
                >
                  Contact Sales
                </button>

              </div>

            </div>

          </div>

        </div>
      </section>


      {/* ============================================
          QUOTE MODAL
      ============================================ */}

      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={closeQuoteModal}
      />
    </>
  );
};

export default OurStory;